import { useEffect, useRef, useState } from 'react';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import SnoozeIcon from '@mui/icons-material/Snooze';
import { Box, Paper, Stack, Button } from '@mui/material';
import { useTitleHook } from 'hooks/page-title.hook';
import { EventsListTab, useEventsList } from 'features/events/events-list';
import { EventsListFilter } from 'features/events/events-filter/events-filter';
import { TasksFilter } from 'features/tasks/tasks-filter';
import { Heading } from 'shared/ui/list-page-heading';
import { NavTabInfo, NavTabs } from 'shared/ui/nav-tabs-events';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useUpdateEventsCompleted } from 'features/events/events-actions/event-actions.hook';
import { useSnack } from 'shared/ui/snack';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { TasksListTab } from '../../pages/tasks/tasks-tab';
import { AddTaskDialog } from 'features/tasks/add-task-dialog';
import { useTasksTabControls } from 'features/tasks/use-tasks-tab-controls';
import { TasksTabActions } from 'features/tasks/tasks-tab-actions';
import AddIcon from '@mui/icons-material/Add';
import AddCaseDialog from 'components/Companies/Feed/add-case-dialog';

const EventsListPage = () => {
  useTitleHook('Список дел');
  const [checkedItems, setCheckedItems] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  const [openAddDialog, setOpenAddDialog] = useState(false);

  const paramsRef = useRef<URLSearchParams>(searchParams);
  const location = useLocation();
  const {
    checkedItems: tasksCheckedItems,
    setCheckedItems: setTasksCheckedItems,
    setPageIds: setTasksCurrentPageIds,
    setCountOnPage: setTasksCountOnPage,
    handleToggleAll: handleTasksToggleAll,
    isAllChecked: areAllTasksChecked,
    isIndeterminate: areTasksIndeterminate,
    countOnPage: tasksCountOnPage,
    taskCreateOpen,
    openTaskCreateDialog,
    closeTaskCreateDialog,
  } = useTasksTabControls();
  const isTasksTab = location.pathname.endsWith('/tasks');
  const isEventsTab = location.pathname.endsWith('/events');
  const initializedTabRef = useRef<string>();

  useEffect(() => {
    if (initializedTabRef.current === location.pathname) return;
    initializedTabRef.current = location.pathname;

    const initialParams = new URLSearchParams(searchParams);
    for (const [key, value] of Array.from(initialParams.entries())) {
      if (value === 'null' || value === 'undefined') {
        initialParams.delete(key);
      }
    }
    if (isEventsTab) {
      initialParams.delete('isCompletedHidden');
    }
    if (!isTasksTab && !isEventsTab && !initialParams.has('isCompletedHidden')) {
      initialParams.set('isCompletedHidden', 'true');
    }

    if (searchParams.toString() !== initialParams.toString()) {
      setSearchParams(initialParams, { replace: true });
    }
  }, [location.pathname, isEventsTab, isTasksTab, searchParams, setSearchParams]);

  useEffect(() => {
    paramsRef.current = searchParams;
    setCurrentPage(0);
  }, [searchParams]);

  const updateEventsCompleted = useUpdateEventsCompleted();
  const { showError, showSuccess } = useSnack();

  const tabInfos: NavTabInfo[] = [
    {
      path: 'all',
      label: 'Все дела',
      icon: <FormatAlignJustifyIcon />,
      component: <EventsListTab tab="all" checkedItems={checkedItems} setCheckedItems={setCheckedItems} searchParams={searchParams} />,
    },
    {
      path: 'my',
      label: 'Мои дела',
      icon: <SelfImprovementIcon />,
      component: <EventsListTab tab="my" checkedItems={checkedItems} setCheckedItems={setCheckedItems} searchParams={searchParams} />,
    },
    {
      path: 'overdue',
      label: 'Просроченные дела',
      icon: <SnoozeIcon />,
      component: <EventsListTab tab="overdue" checkedItems={checkedItems} setCheckedItems={setCheckedItems} searchParams={searchParams} />,
    },
    {
      path: 'events',
      label: 'События',
      icon: <EventNoteIcon />,
      component: <EventsListTab tab="events" checkedItems={checkedItems} setCheckedItems={setCheckedItems} searchParams={searchParams} />,
    },
    {
      path: 'tasks',
      label: 'Задачи',
      icon: <FactCheckIcon />,
      component: (
        <TasksListTab
          key="tasks"
          checkedItems={tasksCheckedItems}
          setCheckedItems={setTasksCheckedItems}
          onPageIdsChange={setTasksCurrentPageIds}
          onCountChange={setTasksCountOnPage}
        />
      ),
    },
  ];

  const { collection, isLoading, error } = useEventsList('all', currentPage, paramsRef.current, { skip: isTasksTab });

  const handleEventsToggleAll = (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const currentPageIds = collection.items.map((item) => item.id);

    if (checked) {
      setCheckedItems((prev) => [...new Set([...prev, ...currentPageIds])]);
    } else {
      setCheckedItems((prev) => prev.filter((id) => !currentPageIds.includes(id)));
    }
  };

  const areAllEventsChecked = collection.items.length > 0 && collection.items.every((item) => checkedItems.includes(item.id));

  const areEventsIndeterminate = !areAllEventsChecked && collection.items.some((item) => checkedItems.includes(item.id));

  const handleMarkAsDone = async () => {
    if (checkedItems.length > 0) {
      try {
        await updateEventsCompleted(checkedItems);
        setCheckedItems([]);
        showSuccess('Статус выбранных дел изменён на "Выполнено"');
      } catch (e) {
        showError('Статус выбранных дел не изменился на "Выполнено": ' + extractNestJsErrorMessage(e));
      }
    }
  };

  const handleAddClick = () => {
    setOpenAddDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenAddDialog(false);
  };

  const handleCaseAdded = () => {
    setOpenAddDialog(false);
    setCheckedItems([]);
  };

  return (
    <Stack direction="column">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Heading sx={{ mb: 2 }} title="Список дел" />
        <Button onClick={handleAddClick} variant="contained" startIcon={<AddIcon />} size="small">
          Добавить
        </Button>
      </Box>
      <AddCaseDialog open={openAddDialog} onClose={handleCloseDialog} onCaseAdded={handleCaseAdded} />

      <Paper sx={{ p: 1 }}>
        {isTasksTab ? (
          <TasksFilter />
        ) : (
          <EventsListFilter
            disabledTaskType={isEventsTab}
            isCompletedHidden={!isEventsTab}
            showCompanyFilter
            showDirectionFilter={isEventsTab}
          />
        )}
      </Paper>
      <NavTabs
        tabs={tabInfos}
        checkedItems={isTasksTab ? tasksCheckedItems : checkedItems}
        isAllChecked={isTasksTab ? areAllTasksChecked : areAllEventsChecked}
        isIndeterminate={isTasksTab ? areTasksIndeterminate : areEventsIndeterminate}
        countElements={isTasksTab ? tasksCountOnPage : collection.items.length}
        handleToggleAll={(e, checked) => {
          if (isTasksTab) {
            handleTasksToggleAll(checked);
          } else {
            handleEventsToggleAll(e, checked);
          }
        }}
        handleMarkAsDone={handleMarkAsDone}
        hideHeaderCheckbox={isTasksTab || isEventsTab}
        renderActions={({ checkedItems: currentChecked }) => {
          if (isEventsTab) {
            return null;
          }
          if (isTasksTab) {
            return <TasksTabActions checkedItems={currentChecked} onClearSelection={() => setTasksCheckedItems([])} onOpenCreate={openTaskCreateDialog} />;
          }
          // default for events
          return (
            <Button variant="contained" size="small" disabled={currentChecked.length === 0} onClick={handleMarkAsDone}>
              Выполнено
            </Button>
          );
        }}
      />
      <AddTaskDialog open={taskCreateOpen} onClose={closeTaskCreateDialog} />
    </Stack>
  );
};
export default EventsListPage;
